// Handle-input typeahead (DOM + network glue), ported from p2pds' account-search onto vanilla TS.
// As the user types a partial handle, query the public appview's unauthenticated typeahead and show a
// keyboard-navigable dropdown; picking one fills the input and calls `onPick`. The load-bearing bits
// (the query URL and the untrusted-JSON → suggestion mapping) live in derive.ts and are unit-tested;
// this file is DOM wiring only (typecheck-only, like main.ts).
import { actorSuggestions, typeaheadUrl, type HandleSuggestion } from "./derive.ts";

const DEBOUNCE_MS = 250;

export interface TypeaheadRefs {
  input: HTMLInputElement;
  list: HTMLUListElement;
}

/**
 * Wire live handle suggestions onto `refs.input`, rendering into `refs.list`. Picking a suggestion
 * (click or Enter on the active row) fills the input and invokes `onPick(handle)`.
 */
export function attachHandleTypeahead(refs: TypeaheadRefs, onPick: (handle: string) => void): void {
  const { input, list } = refs;
  let items: HandleSuggestion[] = [];
  let active = -1;
  let debounce: ReturnType<typeof setTimeout> | undefined;
  // Monotonic token so a slow response for an older query can't clobber a newer one (p2pds lacked this).
  let seq = 0;

  function hide(): void {
    list.classList.add("hidden");
    active = -1;
  }

  function renderList(): void {
    list.replaceChildren();
    if (items.length === 0) {
      hide();
      return;
    }
    items.forEach((s, i) => {
      const li = document.createElement("li");
      if (i === active) li.className = "active";
      li.title = s.handle;

      const img = document.createElement("img");
      img.className = "suggestion-avatar";
      img.alt = "";
      if (s.avatar !== undefined) img.src = s.avatar;

      const text = document.createElement("span");
      text.className = "suggestion-text";
      text.textContent = s.label === s.handle ? `@${s.handle}` : `${s.label} · @${s.handle}`;

      li.append(img, text);
      // mousedown (not click) so the pick lands before the input's blur hides the list.
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        pick(i);
      });
      list.append(li);
    });
    list.classList.remove("hidden");
  }

  function pick(i: number): void {
    const s = items[i];
    if (s === undefined) return;
    input.value = s.handle;
    items = [];
    hide();
    onPick(s.handle);
  }

  async function search(q: string): Promise<void> {
    const mine = ++seq;
    try {
      const res = await fetch(typeaheadUrl(q));
      if (!res.ok || mine !== seq) return;
      const body: unknown = await res.json();
      if (mine !== seq) return; // a newer keystroke superseded this response
      items = actorSuggestions(body);
      active = -1;
      renderList();
    } catch {
      // Suggestions are best-effort; a failed lookup just shows nothing.
    }
  }

  input.addEventListener("input", () => {
    const q = input.value.trim();
    if (debounce !== undefined) clearTimeout(debounce);
    // A full PDS URL or a DID is not a handle to type-ahead — skip the lookup.
    if (q === "" || q.includes("://") || q.startsWith("did:")) {
      items = [];
      hide();
      return;
    }
    // Debounce is a UX rate-limit at the input boundary (throttling network calls), not a race workaround.
    debounce = setTimeout(() => void search(q), DEBOUNCE_MS);
  });

  input.addEventListener("keydown", (e) => {
    if (list.classList.contains("hidden") || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      active = Math.min(active + 1, items.length - 1);
      renderList();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      active = Math.max(active - 1, 0);
      renderList();
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault(); // pick the highlighted suggestion instead of submitting the form
      pick(active);
    } else if (e.key === "Escape") {
      hide();
    }
  });

  // mousedown on a row fires before blur, so hiding here can't race the pick.
  input.addEventListener("blur", () => hide());
}
